/* eslint-disable @typescript-eslint/naming-convention */
import { BetDrawPeriodValue } from "../models/bet-online/draw-selector-attributes";
import { FutureDrawDto } from "../models/draw-info/future-draw-dto";
import { Lottery } from "../models/lottery";
import { FtBoardNumberDto } from "../my-tickets/favotire-tickets/models/ft-models";

/**
 * This namespace provides utility functions for parsing and formatting lottery-related information.
 */
export namespace LotteryParser {
    export const primaryMapingsRychla6 = [[], ["", "Lichá", "Stejně", "Sudá"], ["", "Nízká", "Stejně", "Vysoká"]];
    export const titlesRychla6 = ["Hlavní hra", "Sudá nebo Lichá", "Nízká nebo Vysoká"];
    export const titlesRychla6OneLine = ["Hlavní hra", "Sudá nebo Lichá", "Nízká nebo Vysoká"];
    export const titleVNN = "Hlavní hra";
    export const titlesDataTestid = ["mainGame", "evenOddGame", "lowHighGame"];

    /**
     * Parses a lottery name string and returns the corresponding Lottery enum value.
     * @param gameName - The name of the lottery as a string.
     * @returns The corresponding Lottery enum value, or null if not found.
     */
    export const parseLotteryName = (gameName: string) => {
        switch (gameName?.toLowerCase()) {
            case "euromiliony":
            case "euromilliony":
                return Lottery.euromiliony;

            case "extra renta":
            case "extrarenta":
            case "extra-renta":
                return Lottery.extraRenta;

            case "extrarentamh":
                return Lottery.extraRentaMh;

            case "rychle-kacky":
            case "rychle kacky":
            case "rychlekacky":
                return Lottery.rychleKacky;

            case "sazkamobil sance":
            case "sazkamobilsance":
            case "sazkamobil-sance":
            case "sazkamobil":
            case "sazka-mobil":
            case "smobil":
            case "mobil":
                return Lottery.sazkaMobil;

            case "stastne datum":
            case "stastnedatum":
            case "stastne-datum":
                return Lottery.stastneDatum;

            case "stastnych 10":
            case "šťastných 10":
            case "stastnych10":
            case "stastnych-10":
                return Lottery.stastnych10;

            case "toto sazka":
            case "totosazka":
            case "toto-sazka":
            case "toto":
            case "sazka":
                return Lottery.totoSazka;

            case "rychla-6":
            case "rychla6":
            case "rychla 6":
                return Lottery.rychla6;

            case "vsechno-nebo-nic":
            case "všechno nebo nic":
            case "vsechnonebonic":
            case "vnn":
                return Lottery.vsechnoNeboNic;

            case "minirenta":
            case "mini-renta":
            case "mini renta":
                return Lottery.miniRenta;

            case "power-spin":
            case "powerspin":
            case "power spin":
                return Lottery.powerSpin;

            default:
                if (Object.values(Lottery).includes(gameName?.toLowerCase() as Lottery)) {
                    return gameName?.toLowerCase() as Lottery;
                }
                return null;
        }
    };

    /**
     * Retrieves the code name of a lottery from its enum value.
     *
     * @param game - The Lottery enum value representing the lottery.
     * @returns The string representation of the lottery's code name.
     */
    export const lotteryCodeName = (game: Lottery): string => {
        return Lottery[game];
    };

    /**
     * Returns the display name for a given lottery.
     * @param game - The lottery enum value.
     * @returns The formatted display name of the lottery.
     */
    export const lotteryDisplayName = (game: Lottery) => {
        switch (game) {
            case Lottery.eurojackpot:
                return "Eurojackpot";
            case Lottery.euromiliony:
                return "Euromiliony";
            case Lottery.extraRenta:
            case Lottery.extraRentaMh:
                return "Extra renta";
            case Lottery.kameny:
                return "Kameny";
            case Lottery.kasicka:
                return "Kasička";
            case Lottery.keno:
                return "Keno";
            case Lottery.rychleKacky:
                return "Rychlé kačky";
            case Lottery.sazkaMobil:
                return "SAZKAmobil šance";
            case Lottery.sportka:
            case Lottery.sportkaMh:
                return "Sportka";
            case Lottery.stastneDatum:
                return "Šťastné datum";
            case Lottery.stastnych10:
                return "Šťastných 10";
            case Lottery.totoSazka:
                return "TOTO Sazka";
            case Lottery.sance:
            case Lottery.sanceMh:
                return "Šance";
            case Lottery.sanceMilion:
                return "Šance milion";
            case Lottery.extra6:
                return "Extra 6";
            case Lottery.euroSance:
                return "Eurošance";
            case Lottery.rychla6:
                return "Rychlá 6";
            case Lottery.vsechnoNeboNic:
                return "Všechno\n nebo nic";
            case Lottery.miniRenta:
                return "Mini renta";
            case Lottery.powerSpin:
                return "Power Spin";
            case Lottery.all:
                return "Vše";
            default:
                return game;
        }
    };

    /**
     * Returns the display name of a lottery in the 2nd grammatical case (genitive) in Czech.
     *
     * @param game - The lottery enum value for which to get the display name.
     * @returns The formatted display name of the lottery in the 2nd grammatical case (genitive).
     *          If no specific 2nd case form is defined, it returns the default display name.
     */
    export const lotteryDisplayName2ndFall = (game: Lottery) => {
        switch (game) {
            case Lottery.extraRenta:
            case Lottery.extraRentaMh:
                return "Extra renty";
            case Lottery.kasicka:
                return "Kasičky";
            case Lottery.sazkaMobil:
                return "SAZKAmobil šance";
            case Lottery.sportka:
            case Lottery.sportkaMh:
                return "Sportky";
            case Lottery.totoSazka:
                return "TOTO Sazky";
            case Lottery.sance:
            case Lottery.sanceMh:
                return "Šance";
            case Lottery.sanceMilion:
                return "Šance milion";
            case Lottery.euroSance:
                return "Eurošance";
            case Lottery.rychla6:
                return "Rychlé 6";
            case Lottery.miniRenta:
                return "Mini renty";
            case Lottery.kameny:
                return "Kamenů";
            case Lottery.keno:
                return "Kena";
            case Lottery.powerSpin:
                return "Power Spinu";
            default:
                return lotteryDisplayName(game);
        }
    };

    /**
     * Returns the display name of a lottery in the 4th grammatical case (accusative).
     * @param game - The lottery enum value.
     * @returns The formatted display name in the 4th case.
     */
    export const lotteryDisplayName4thFall = (game: Lottery) => {
        switch (game) {
            case Lottery.extraRenta:
            case Lottery.extraRentaMh:
                return "Extra rentu";
            case Lottery.kasicka:
                return "Kasičku";
            case Lottery.sazkaMobil:
                return "SAZKAmobil šanci";
            case Lottery.sportka:
            case Lottery.sportkaMh:
                return "Sportku";
            case Lottery.totoSazka:
                return "TOTO Sazku";
            case Lottery.sance:
            case Lottery.sanceMh:
                return "Šanci";
            case Lottery.sanceMilion:
                return "Šanci milion";
            case Lottery.euroSance:
                return "Eurošanci";
            case Lottery.rychla6:
                return "Rychlou 6";
            case Lottery.miniRenta:
                return "Mini rentu";
            default:
                return lotteryDisplayName(game);
        }
    };
    /**
     * Returns the display name of a lottery in the 6th grammatical case (accusative).
     * @param game - The lottery enum value.
     * @returns The formatted display name in the 6th case.
     */
    export const lotteryDisplayName6thFall = (game: Lottery) => {
        switch (game) {
            case Lottery.eurojackpot:
                return "Eurojackpotu";
            case Lottery.euromiliony:
                return "Euromilionech";
            case Lottery.extraRenta:
            case Lottery.extraRentaMh:
                return "Extra rentě";
            case Lottery.kameny:
                return "Kamenech";
            case Lottery.kasicka:
                return "Kasičce";
            case Lottery.keno:
                return "Kenu";
            case Lottery.rychleKacky:
                return "Rychlých kačkách";
            case Lottery.sazkaMobil:
                return "SAZKAmobil šanci";
            case Lottery.sportka:
            case Lottery.sportkaMh:
                return "Sportce";
            case Lottery.stastneDatum:
                return "Šťastném datumu";
            case Lottery.stastnych10:
                return "Šťastných 10";
            case Lottery.totoSazka:
                return "TOTO Sazce";
            case Lottery.sance:
            case Lottery.sanceMh:
                return "Šanci";
            case Lottery.sanceMilion:
                return "Šanci milion";
            case Lottery.extra6:
                return "Extra 6";
            case Lottery.euroSance:
                return "Eurošanci";
            case Lottery.rychla6:
                return "Rychlých 6";
            case Lottery.vsechnoNeboNic:
                return "Všechno\n nebo nic";
            case Lottery.miniRenta:
                return "Mini rentě";
            case Lottery.powerSpin:
                return "Power Spinu";
            default:
                return game;
        }
    };

    export const lottery6thFallWithPreposition = (game: Lottery) => {
        let preposition = "v";
        if (
            game == Lottery.sportka ||
            game == Lottery.stastneDatum ||
            game == Lottery.stastnych10 ||
            game == Lottery.vsechnoNeboNic
        ) {
            preposition = "ve";
        }

        return `${preposition} ${lotteryDisplayName6thFall(game)}`;
    };

    /**
     * Returns the analytics code name for a given lottery.
     * @param game - The lottery enum value.
     * @returns The analytics code name.
     */
    export const analyticsCodeName = (game: Lottery) => {
        switch (game) {
            case Lottery.stastnych10:
                return "stastnych10";
            case Lottery.sportkaMh:
                return "sportka";
            case Lottery.rychla6:
                return "rychla6";
            default:
                return game;
        }
    };

    /**
     * Gets the addon lottery for a given main lottery.
     * @param game - The main lottery enum value.
     * @returns The addon lottery enum value, or null if no addon exists.
     */
    export const getAddon = (game: Lottery) => {
        switch (game) {
            case Lottery.sportka:
                return Lottery.sance;
            case Lottery.sportkaMh:
                return Lottery.sanceMh;
            case Lottery.stastnych10:
                return Lottery.sanceMilion;
            case Lottery.eurojackpot:
                return Lottery.extra6;
            case Lottery.euromiliony:
                return Lottery.euroSance;
            default:
                return null;
        }
    };

    /**
     * Returns the name of the addon lottery in the 4th grammatical case (accusative) for a given lottery.
     *
     * @param game - The lottery enum value for which to get the addon name.
     * @returns The name of the addon lottery in the 4th grammatical case, or null if no addon exists for the given lottery.
     */
    export const getAddonName4thFall = (game: Lottery) => {
        switch (game) {
            case Lottery.sportka:
            case Lottery.sance:
            case Lottery.sportkaMh:
            case Lottery.sanceMh:
                return "Šanci";
            case Lottery.stastnych10:
            case Lottery.sanceMilion:
                return "Šanci milion";
            case Lottery.eurojackpot:
            case Lottery.extra6:
                return "Extra 6";
            case Lottery.euromiliony:
            case Lottery.euroSance:
                return "Eurošanci";
            default:
                return null;
        }
    };

    /**
     * Returns the display name of the addon lottery for a given main lottery.
     *
     * @param game - The main lottery enum value for which to get the addon display name.
     * @returns The display name of the addon lottery as a string. Returns an empty string if no addon exists for the given lottery.
     */
    export const addonDisplayName = (game: Lottery) => {
        switch (game) {
            case Lottery.sportka:
            case Lottery.sportkaMh:
            case Lottery.sance:
            case Lottery.sanceMh:
                return lotteryDisplayName(Lottery.sance);
            case Lottery.stastnych10:
            case Lottery.sanceMilion:
                return lotteryDisplayName(Lottery.sanceMilion);
            case Lottery.eurojackpot:
            case Lottery.extra6:
                return lotteryDisplayName(Lottery.extra6);
            case Lottery.euromiliony:
            case Lottery.euroSance:
                return lotteryDisplayName(Lottery.euroSance);
            default:
                return "";
        }
    };

    export const getSyndicateOnlineCookieName = (lottery: Lottery) => {
        switch (lottery) {
            case Lottery.sportka:
            case Lottery.sportkaMh:
                return "sportka-online-tipnums-syn";
            case Lottery.eurojackpot:
                return "eurojackpot-online-tipnums-syn";
        }
    };

    export const getSyndicateDLCookieName = () => {
        return "syndicate-filling";
    };

    export const getDrawNamesDisplayNames = (drawNames: string[], lottery: Lottery): string => {
        if (lottery == Lottery.extraRenta && !drawNames?.length) {
            return "Pondělí";
        }

        if (lottery == Lottery.miniRenta) {
            return "Každý den";
        }

        const displayNames = drawNames?.map((d) => {
            switch (d.toLowerCase()) {
                case "mon":
                    return "Pondělí";
                case "tue":
                    return "Úterý";
                case "wed":
                    return "Středa";
                case "thu":
                    return "Čtvrtek";
                case "fri":
                    return "Pátek";
                case "sat":
                    return "Sobota";
                case "sun":
                    return "Neděle";
                case "afternoon":
                    if (lottery == Lottery.stastnych10) {
                        return "Poledne";
                    } else {
                        return "Odpoledne";
                    }
                case "evening":
                    return "Večer";
                default:
                    return d;
            }
        });

        return displayNames?.join(", ");
    };

    export const getDrawNameValue = (betDate: FutureDrawDto, isSpecial = false): BetDrawPeriodValue => {
        if (isSpecial) {
            return "special";
        }
        if (betDate.drawIndex == "NOON") {
            return "afternoon";
        }
        if (betDate.drawIndex == "EVENING") {
            return "evening";
        }

        return new Intl.DateTimeFormat("en-UK", { weekday: "short" })
            .format(new Date(betDate.drawDate))
            .toLowerCase() as BetDrawPeriodValue;
    };

    export const getDrawLabel = (lottery: Lottery, index = 0) => {
        switch (lottery) {
            case Lottery.sportka:
                return `${index + 1}. tah`;
            case Lottery.eurojackpot:
            case Lottery.euromiliony:
                return `${index + 1}. osudí`;
            case Lottery.stastneDatum:
                switch (index) {
                    case 0:
                        return "den";
                    case 1:
                        return "měsíc";
                    default:
                        return "rok";
                }
            default:
                return null;
        }
    };

    export const boardLabel = (game: Lottery) => {
        switch (game) {
            case Lottery.kasicka:
            case Lottery.kameny:
            case Lottery.keno:
                return "hrací pole";
            default:
                return "sloupec";
        }
    };

    export const drawLabel = (game: Lottery, date: Date, drawId: number) => {
        switch (game) {
            case Lottery.keno:
            case Lottery.kameny:
            case Lottery.rychleKacky:
            case Lottery.rychla6:
            case Lottery.vsechnoNeboNic:
            case Lottery.powerSpin:
                return drawId ? ` (č.&nbsp;${drawId}) ` : " ";
            case Lottery.kasicka:
                if (!date) return " ";
                return date.getHours() < 15 ? " (odpoledne) " : " (večer) ";
            case Lottery.stastnych10:
                if (!date) return " ";
                return date.getHours() < 13 ? " (poledne) " : " (večer) ";
            default:
                return " ";
        }
    };

    export const boardOrderLabel = (game: Lottery, boardOrder: number, mobile = false) => {
        switch (game) {
            case Lottery.kasicka:
            case Lottery.kameny:
            case Lottery.keno:
                return `${boardOrder}. hrací pole`;
            case Lottery.extraRenta:
            case Lottery.miniRenta:
            case Lottery.rychleKacky:
                return mobile ? `${boardOrder}. sloupec` : `${boardOrder}.`;
            case Lottery.stastnych10:
                return `${boardOrder}. sloupec`;
            case Lottery.powerSpin:
                return `${boardOrder}. Power Spin`;
            default:
                return `${boardOrder}.`;
        }
    };

    export const boardInfoLabel = (board: FtBoardNumberDto) => {
        if (!board.primary || board.primary.length == 0) {
            return "";
        } else {
            /// board description text
            let statusLineText = board.primary.length.toString();
            switch (board.primary.length) {
                case 1:
                    statusLineText += " číslo";
                    break;
                case 2:
                case 3:
                case 4:
                    statusLineText += " čísla";
                    break;
                default:
                    statusLineText += " čísel";
                    break;
            }

            statusLineText += board.stake > 0 ? ", Vklad " + board.stake + " Kč" : "";
            statusLineText += board.kralovskaHra ? ", Královská hra" : "";
            return statusLineText;
        }
    };

    export const boardLabelPlural = (game: Lottery, boardCount: number) => {
        switch (game) {
            case Lottery.kasicka:
            case Lottery.kameny:
            case Lottery.keno:
                return boardCount > 0 && boardCount < 5 ? `${boardCount} hrací pole` : `${boardCount} hracích polí`;
            default:
                switch (boardCount) {
                    case 1:
                        return "1 sloupec";
                    case 2:
                    case 3:
                    case 4:
                        return `${boardCount} sloupce`;
                    default:
                        return `${boardCount} sloupců`;
                }
        }
    };

    export const getPowerSpinGameLabel = (systemBetType: string) => {
        switch (systemBetType) {
            case "Red":
                return "Červená";
            case "Green":
                return "Zelená";
            case "Blue":
                return "Modrá";
            case "High":
                return "Vysoká čísla";
            case "Low":
                return "Nízká čísla";
            case "Symbol":
                return "Symbol";
            case "1":
                return "1 číslo";
            case "2":
            case "3":
            case "4":
                return `${systemBetType} čísla`;
            default:
                return `${systemBetType} čísel`;
        }
    };

    export const getNumberLabel = (x: number) => {
        if (x == 1) {
            return "číslo";
        } else if (x > 1 && x < 5) {
            return "čísla";
        } else {
            return "čísel";
        }
    };

    export const getNumberWithLabel = (x: number) => `${x} ${getNumberLabel(x)}`;

    export function getDayInflectionLabel(dayCount: number) {
        switch (dayCount) {
            case 1:
                return "den";
            case 2:
            case 3:
            case 4:
                return "dny";
            default:
                return "dní";
        }
    }
}
